import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { openCookieSettings } from '@/lib/consent';

const ROWS = [
  ['Necessários', <>Sessão iniciada e segurança, verificação de idade (+18), tema claro/escuro e registo da tua escolha de cookies (<code>intimate_consent</code>, guardado no armazenamento local do navegador).</>, 'Sessão / até 1 ano', 'Não necessário (estritamente necessários)'],
  ['Estatísticas', <>Cookie próprio <code>intimate_visitor</code>, para distinguir visitas novas e recorrentes. Google Analytics 4 (<code>_ga</code>), apenas se for ativado.</>, <>1 ano (<code>intimate_visitor</code>); até 2 anos (GA4)</>, 'Sim, só depois de aceitares'],
  ['Gravação de sessão', <>Microsoft Clarity (<code>_clck</code>, <code>_clsk</code>): mapas de cliques e gravação da navegação, apenas se for ativado.</>, 'Até 1 ano', 'Sim, só depois de aceitares'],
];

export default function CookiePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Voltar
        </Link>

        <div className="prose prose-invert max-w-none">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Política de Cookies</h1>
          <p className="text-muted-foreground mb-8">Última atualização: Junho de 2026 · Consentimento v1</p>

          <div className="space-y-8 text-muted-foreground">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">1. O que são Cookies?</h2>
              <p>
                Cookies são pequenos ficheiros de texto guardados no teu dispositivo (computador, tablet ou telemóvel) quando
                visitas um website. Tecnologias semelhantes, como o armazenamento local do navegador, seguem as mesmas regras.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">2. Como Utilizamos Cookies</h2>
              <p>
                Usamos cookies estritamente necessários para o site funcionar. Cookies de estatística e de gravação de sessão só
                são usados se os aceitares no aviso de cookies. Recusar não limita nenhuma funcionalidade.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">3. Categorias e Cookies que Utilizamos</h2>
              <div className="overflow-x-auto mt-6">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      {['Categoria', 'Finalidade e ferramenta', 'Duração', 'Consentimento'].map((h) => (
                        <th key={h} className="text-left py-3 px-4 text-foreground font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ROWS.map(([cat, fin, dur, cons]) => (
                      <tr key={cat} className="border-b border-border/50">
                        <td className="py-3 px-4"><strong className="text-foreground">{cat}</strong></td>
                        <td className="py-3 px-4">{fin}</td>
                        <td className="py-3 px-4">{dur}</td>
                        <td className="py-3 px-4">{cons}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">4. Estatísticas sem Cookies</h2>
              <p>
                Se recusares, ou ainda não tiveres escolhido, não guardamos nem lemos nenhum cookie de estatística no teu dispositivo.
                Para sabermos quantas visitas o site recebe, somamos apenas um contador diário agregado com: data, secção geral do site
                (por exemplo "Contos" ou "+ Prazer", nunca o conteúdo concreto), origem da visita (por exemplo Google ou Instagram) e tipo
                de dispositivo (computador, telemóvel ou tablet). Não guardamos o teu IP, a identificação do navegador, a tua conta nem
                qualquer outro identificador. Estes contadores não permitem identificar ninguém.
              </p>
              <p className="mt-3">As páginas que visitas nunca são associadas à tua conta para fins estatísticos.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">5. Cookies de Terceiros</h2>
              <p>
                O Google Analytics 4 (Google Ireland Ltd.) e o Microsoft Clarity (Microsoft Ireland Operations Ltd.) só são carregados
                se forem ativados pela Intimate.pt <strong className="text-foreground">e</strong> se aceitares a respetiva categoria.
                Estes fornecedores podem tratar dados fora do Espaço Económico Europeu, ao abrigo das garantias que oferecem.
                Os pagamentos são tratados pelos respetivos prestadores, segundo as suas próprias políticas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">6. Conservação</h2>
              <p>
                O cookie <code>intimate_visitor</code> expira ao fim de 1 ano. As visitas registadas com consentimento são conservadas
                para estatísticas históricas. Os contadores anónimos
                não contêm dados pessoais.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">7. Como Alterar ou Retirar o Consentimento</h2>
              <p>
                Podes aceitar, recusar ou personalizar a qualquer momento em{' '}
                <button type="button" onClick={openCookieSettings} className="text-ruby hover:underline" data-testid="cookie-policy-open-settings">
                  Definições de cookies
                </button>{' '}
                (também no rodapé do site). Se retirares o consentimento de estatísticas, apagamos o cookie <code>intimate_visitor</code>,
                deixamos de recolher dados com ele e retiramos esse identificador das visitas anteriores, que passam a ser anónimas.
                Também apagamos os cookies do Google Analytics e do Clarity que o nosso domínio consegue remover. Podes ainda apagar
                cookies nas definições do teu navegador.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">8. Alterações a Esta Política</h2>
              <p>
                Podemos atualizar esta Política de Cookies. Se as categorias mudarem, voltamos a pedir a tua escolha.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">9. Contacto</h2>
              <p>Para questões sobre a nossa utilização de cookies:</p>
              <p className="mt-3">
                <strong className="text-foreground">Email:</strong> geral@pimentadocelingerie.pt
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4">Documentos Relacionados</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/terms" className="text-ruby hover:underline">Termos de Utilização</Link>
              <Link to="/privacy" className="text-ruby hover:underline">Política de Privacidade</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
